import React, { useState, useEffect } from 'react';
import logo from '../TBL Logo White.png';
import './FinalBlend.css';

const FinalBlend = ({ classVersion, onNext, onBack }) => {
  const [blendName, setBlendName] = useState('');
  const [blendPercentages, setBlendPercentages] = useState({
    wine1: 0,
    wine2: 0,
    wine3: 0
  });

  // Load saved data from session storage
  useEffect(() => {
    const savedBlendName = sessionStorage.getItem('finalBlendName');
    const savedBlendPercentages = sessionStorage.getItem('finalBlendPercentages');
    const savedFinalBlendData = sessionStorage.getItem('finalBlendData');
    
    console.log('FinalBlend: Loading saved data:', {
      savedBlendName,
      savedBlendPercentages,
      savedFinalBlendData
    });
    
    if (savedBlendName) {
      setBlendName(savedBlendName);
    }
    if (savedBlendPercentages) {
      try {
        setBlendPercentages(JSON.parse(savedBlendPercentages));
      } catch (error) {
        console.error('FinalBlend: Error parsing saved percentages:', error);
      }
    }
  }, []);

  // Save data to session storage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('finalBlendName', blendName);
    
    // Also save the complete finalBlendData structure
    const finalBlendData = {
      blendName,
      percentages: blendPercentages,
      timestamp: new Date().toISOString(),
      classVersion
    };
    console.log('FinalBlend: Saving finalBlendData:', finalBlendData);
    sessionStorage.setItem('finalBlendData', JSON.stringify(finalBlendData));
  }, [blendName, blendPercentages, classVersion]);

  useEffect(() => {
    sessionStorage.setItem('finalBlendPercentages', JSON.stringify(blendPercentages));
  }, [blendPercentages]);

  const handlePercentageChange = (wine, percentage) => {
    setBlendPercentages(prev => ({
      ...prev,
      [wine]: percentage
    }));
  };

  const handleNext = () => {
    // Store final blend data for analytics
    const finalBlendData = {
      blendName,
      percentages: blendPercentages,
      timestamp: new Date().toISOString(),
      classVersion
    };
    
    sessionStorage.setItem('finalBlendData', JSON.stringify(finalBlendData));
    onNext();
  };

  const wines = [
    { id: 'wine1', name: 'Wine 1' },
    { id: 'wine2', name: 'Wine 2' },
    { id: 'wine3', name: 'Wine 3' }
  ];

  // Calculate total percentage
  const totalPercentage = blendPercentages.wine1 + blendPercentages.wine2 + blendPercentages.wine3;

  return (
    <div className={`final-blend ${classVersion}-theme`}>
      <div className="blend-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Navigation
        </button>
        <img src={logo} className="blend-logo" alt="TBL Logo" />
        <h1>Final Blend</h1>
        <p className="blend-description">
          Create your perfect blend by naming it and setting the final percentages for each wine
        </p>
      </div>

      <div className="blend-container">
        <div className="blend-name-section">
          <div className="name-input-container">
            <input
              type="text"
              value={blendName}
              onChange={(e) => setBlendName(e.target.value)}
              placeholder="Enter your blend name..."
              className="blend-name-input"
              maxLength={50}
            />
            <div className="name-separator-line"></div>
            <h2>Name Your Blend</h2>
          </div>
        </div>

        <div className="blend-percentages-section">
          <h2>Set Your Blend Percentages</h2>
          <p className="percentage-instruction">
            Adjust the sliders to set the percentage of each wine in your final blend (0-100%)
          </p>
          
          <table className="wines-percentage-table">
            <tbody>
              {wines.map((wine) => (
                <tr key={wine.id} className="wine-percentage-row">
                  <td className="wine-name-cell">
                    <h3 className="wine-name">{wine.name}</h3>
                  </td>
                  <td className="percentage-cell">
                    <div className="percentage-container">
                      <div className="slider-container">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={blendPercentages[wine.id]}
                          onChange={(e) => handlePercentageChange(wine.id, parseInt(e.target.value))}
                          className="percentage-slider"
                        />
                        <div className="slider-track"></div>
                        <div className="slider-ticks">
                          {[0, 25, 50, 75, 100].map((tick) => {
                            // Browser formula: (value - min) / (max - min) * (trackWidth - thumbWidth) + thumbWidth/2
                            // For 0-100 range: (tick - 0) / (100 - 0) * (100% - thumbWidth) + thumbWidth/2
                            // Desktop: (tick / 100) * (100% - 28px) + 14px
                            // Mobile: (tick / 100) * (100% - 32px) + 16px
                            const desktopPosition = `calc(${(tick / 100) * 100}% - ${(tick / 100) * 28}px + 14px)`;
                            const mobilePosition = `calc(${(tick / 100) * 100}% - ${(tick / 100) * 32}px + 16px)`;
                            
                            return (
                              <div key={tick} className="slider-tick" style={{ 
                                left: desktopPosition,
                                '--mobile-position': mobilePosition
                              }}>
                                <div className="slider-tick-label">{tick}%</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="percentage-value">
                        <span className="current-percentage">{blendPercentages[wine.id]}%</span>
                        <span className="ml-amount">{Math.round((blendPercentages[wine.id] / 100) * 375)}mL</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="total-percentage">
            <div className={`total-display ${totalPercentage === 100 ? 'perfect' : totalPercentage > 100 ? 'over' : 'under'}`}>
              <span className="total-label">Total:</span>
              <span className="total-value">{totalPercentage}%</span>
              {totalPercentage === 100 && <span className="total-status">✓ Perfect!</span>}
              {totalPercentage > 100 && <span className="total-status">⚠ Over 100%</span>}
              {totalPercentage < 100 && <span className="total-status">⚠ Under 100%</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="action-buttons">
        <button 
          className="next-button" 
          onClick={handleNext}
          disabled={!blendName.trim() || totalPercentage !== 100}
        >
          Continue to Next Section
        </button>
      </div>

      <div className="footer">
        <div className="footer-separator"></div>
        <img src={logo} className="footer-logo" alt="TBL Logo" />
      </div>
    </div>
  );
};

export default FinalBlend;
